import { NavLink } from "react-router-dom";
import { useMemo } from "react";
import { LogIn, LogOut, Lock } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useUnlocks } from "../lib/unlocks";
import Logo from "./Logo";

const links = [
  { to: "/", label: "Catalogue", end: true, testid: "nav-catalogue" },
  { to: "/timeline", label: "Timeline", testid: "nav-timeline" },
  { to: "/stats", label: "Stats", testid: "nav-stats" },
  { to: "/recommendations", label: "Recs", testid: "nav-recommendations" },
  { to: "/personality", label: "Persona", testid: "nav-personality", pro: true },
  { to: "/persona/history", label: "History", testid: "nav-history", pro: true },
  { to: "/discover", label: "Discover", testid: "nav-discover", pro: true },
  { to: "/schedule", label: "Schedule", testid: "nav-schedule", pro: true },
  { to: "/import", label: "Import", testid: "nav-import", pro: true },
  { to: "/feed", label: "Feed", testid: "nav-feed", authOnly: true },
  { to: "/premium", label: "Premium", testid: "nav-premium" },
  { to: "/support", label: "Support", testid: "nav-support" },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startSignIn() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Nav() {
  const { user, loading, signOut } = useAuth();
  const { has } = useUnlocks();
  const proUnlocked = has("pro_pass");
  // Memoize the visible link list so we don't re-filter the whole array on
  // every unrelated Nav re-render (e.g. route change). Recomputes only when
  // auth state flips.
  const visibleLinks = useMemo(() => links.filter((l) => !l.authOnly || user), [user]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4 px-6 py-4 md:px-10">
        <NavLink to="/" className="group flex items-center" data-testid="brand-link">
          <Logo className="h-7 md:h-8" />
        </NavLink>

        <nav className="flex flex-1 items-center justify-center gap-1 md:gap-2 overflow-x-auto">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) =>
                `link-underline whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 transition-colors flex items-center gap-1.5 ${
                  isActive ? "text-white active" : "text-neutral-500 hover:text-white"
                }`
              }
            >
              {l.pro && !proUnlocked && <Lock className="h-3 w-3 text-[#FF3B30]" />}
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex flex-shrink-0 items-center gap-2">
          {loading ? (
            <span className="h-8 w-8 animate-pulse border border-white/10 bg-neutral-900" />
          ) : user ? (
            <div className="flex items-center gap-2" data-testid="auth-user">
              <NavLink to="/account" data-testid="nav-account" className="flex items-center gap-2 hover:opacity-80">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-8 w-8 border border-white/20 object-cover" />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center border border-white/20 bg-neutral-900 font-mono text-[11px] text-white">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="hidden flex-col text-right md:flex">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white">
                    {user.handle ? `@${user.handle}` : user.name || user.email}
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Account</span>
                </div>
              </NavLink>
              <button
                onClick={signOut}
                data-testid="sign-out-btn"
                className="border border-white/15 p-2 text-neutral-400 hover:bg-white hover:text-black"
                title="Sign out"
              >
                <LogOut className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button
              data-testid="sign-in-btn"
              onClick={startSignIn}
              className="flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              <LogIn className="h-3 w-3" />
              Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
